import { useEffect, useState } from 'react'
import { create } from 'zustand'

interface ConnectivityState {
  /**
   * Momento (epoch ms) en que se guardaron los datos que se muestran cuando
   * el service worker respondió desde caché; `null` si los datos son frescos.
   */
  staleSince: number | null
  /** Registra que una respuesta vino de la caché sin conexión. */
  markStale: (cachedAt: number) => void
  /** Registra que llegó una respuesta fresca del servidor. */
  markFresh: () => void
}

/** Frescura de los datos mostrados (la alimenta el cliente del API). */
export const useConnectivityStore = create<ConnectivityState>((set, get) => ({
  staleSince: null,
  markStale(cachedAt) {
    const current = get().staleSince
    // Se conserva el dato más antiguo: es el que describe honestamente la pantalla.
    set({ staleSince: current === null ? cachedAt : Math.min(current, cachedAt) })
  },
  markFresh() {
    if (get().staleSince !== null) set({ staleSince: null })
  },
}))

/** Indica si el navegador reporta conexión, actualizándose con los eventos `online`/`offline`. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine))
  useEffect(() => {
    const update = () => setOnline(navigator.onLine)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])
  return online
}
